/**
 * GlobalSearch v3 -- v2's inline dropdown + a PAGES registry (PO review:
 * "search must find the app itself, not just the data" -- security
 * settings, blocklist, inbox must be reachable by typing their name in
 * either language, with synonyms).
 *
 * The registry is self-contained and bilingual: each entry carries its EN
 * and RO display labels plus lowercase keywords (both languages, common
 * synonyms, diacritics-free forms -- RO is typically typed without them).
 * Only PROVEN routes are listed; deep targets use query params the pages
 * understand (?tab= on Settings ships with this commit). Contact -> open
 * drawer and message -> pre-filtered history are the v3b follow-up (they
 * need ?open= / ?recipient= support in their pages first).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Search,
  MessageSquare,
  User,
  Megaphone,
  BookOpen,
  CornerDownLeft,
  ArrowRight,
} from "lucide-react";
import {
  PAGES,
  GUIDES,
  resolveGuideHref,
  type PageEntry,
  type GuideEntry,
} from "@/lib/search-registry";
import {
  fold,
  loadGuidesIndex,
  guideContentMatches,
  type GuideIndexEntry,
} from "@/lib/guides-search";

type MessageHit = {
  id: string;
  recipient: string;
  status: string;
  channel: string;
  created_at: number;
};
type ContactHit = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone_e164: string;
  email: string | null;
};
type CampaignHit = {
  id: string;
  name: string;
  status: string;
  created_at: number;
};
type SearchResponse = {
  q: string;
  messages: MessageHit[];
  contacts: ContactHit[];
  campaigns: CampaignHit[];
};

type FlatItem =
  | { kind: "page"; page: PageEntry }
  | { kind: "message"; hit: MessageHit }
  | { kind: "contact"; hit: ContactHit }
  | { kind: "campaign"; hit: CampaignHit }
  | { kind: "guide"; guide: GuideEntry };

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

async function fetchSearch(q: string): Promise<SearchResponse> {
  const res = await fetch("/api/search?q=" + encodeURIComponent(q), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("search_failed");
  return (await res.json()) as SearchResponse;
}

export default function GlobalSearch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const ro = i18n.resolvedLanguage === "ro";
  const dq = useDebounced(q.trim(), 300);
  const enabled = open && dq.length >= 2;

  // KB-2b: guide CONTENT search via the shared build-time static index
  // (lib/guides-search). Loaded lazily on first open, per language,
  // cached in-module + by the browser; null on any failure -> guides
  // degrade to title matching. Entirely client-side (PO law).
  const { i18n: guidesI18n } = useTranslation();
  const guidesLang = guidesI18n.language === "ro" ? "ro" : "en";
  const [guidesContent, setGuidesContent] =
    useState<GuideIndexEntry[] | null>(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    loadGuidesIndex(guidesLang).then((d) => {
      if (alive) setGuidesContent(d);
    });
    return () => { alive = false; };
  }, [open, guidesLang]);

  const { data, isFetching } = useQuery<SearchResponse, Error>({
    queryKey: ["global-search", dq],
    queryFn: () => fetchSearch(dq),
    enabled,
    staleTime: 30_000,
  });

  // Pages + guides filter locally (instant, zero server involvement).
  const pageHits = useMemo(() => {
    if (dq.length < 2) return [] as PageEntry[];
    const needle = dq.toLowerCase();
    return PAGES.filter(
      (p) =>
        p.en.toLowerCase().includes(needle) ||
        p.ro.toLowerCase().includes(needle) ||
        p.keywords.some((k) => k.includes(needle)),
    ).slice(0, 5);
  }, [dq]);

  // KB-2b (PO 18-Aug: ONE search in the platform): guides match on the
  // FOLDED title (diacritics-insensitive -- "sabloane" finds Sabloane)
  // OR on article content via the shared static index.
  const guideHits = useMemo(() => {
    if (dq.length < 2) return [] as GuideEntry[];
    const needle = fold(dq);
    return GUIDES.filter((g) => {
      if (fold(t(g.titleKey)).includes(needle)) return true;
      if (!guidesContent) return false;
      const path = resolveGuideHref(g.href, guidesLang);
      const entry = guidesContent.find((e) => e.path === path);
      return entry !== undefined && guideContentMatches(entry, needle);
    });
  }, [dq, t, guidesContent, guidesLang]);

  const flat = useMemo<FlatItem[]>(() => {
    const out: FlatItem[] = [];
    for (const page of pageHits) out.push({ kind: "page", page });
    for (const hit of data?.messages ?? []) out.push({ kind: "message", hit });
    for (const hit of data?.contacts ?? []) out.push({ kind: "contact", hit });
    for (const hit of data?.campaigns ?? [])
      out.push({ kind: "campaign", hit });
    for (const guide of guideHits) out.push({ kind: "guide", guide });
    return out;
  }, [pageHits, data, guideHits]);

  // Keep the highlight inside the list as results change.
  useEffect(() => {
    if (index >= flat.length) setIndex(0);
  }, [flat.length, index]);

  // Global Cmd/Ctrl+K -> focus the bar (inline, not a modal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Outside click closes (mousedown, not blur -- blur races item clicks).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (item: FlatItem) => {
    setOpen(false);
    inputRef.current?.blur();
    if (item.kind === "page") navigate(item.page.path);
    else if (item.kind === "campaign") navigate("/campaigns/" + item.hit.id);
    else if (item.kind === "message")
      navigate("/messages?recipient=" + encodeURIComponent(item.hit.recipient));
    else if (item.kind === "contact")
      navigate("/contacts?open=" + encodeURIComponent(item.hit.id));
    else if (item.guide.slug !== undefined)
      // New-standard guides stay IN-APP (sandboxed viewer keeps the
      // sidebar around the article). Legacy/external entries keep the
      // new tab below.
      navigate("/help/guides/" + item.guide.slug);
    else
      window.open(
        resolveGuideHref(item.guide.href, ro ? "ro" : "en"),
        "_blank",
        "noopener",
      );
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const item = flat[index];
      if (item) go(item);
    }
  };

  const sectionLabel = (kind: FlatItem["kind"]): string =>
    kind === "page"
      ? t("searchDialog.section_pages")
      : kind === "message"
        ? t("searchDialog.section_messages")
        : kind === "contact"
          ? t("searchDialog.section_contacts")
          : kind === "campaign"
            ? t("searchDialog.section_campaigns")
            : t("searchDialog.section_guides");

  const itemIcon = (kind: FlatItem["kind"]) =>
    kind === "page" ? (
      <ArrowRight className="h-4 w-4 shrink-0 text-zinc-400" />
    ) : kind === "message" ? (
      <MessageSquare className="h-4 w-4 shrink-0 text-zinc-400" />
    ) : kind === "contact" ? (
      <User className="h-4 w-4 shrink-0 text-zinc-400" />
    ) : kind === "campaign" ? (
      <Megaphone className="h-4 w-4 shrink-0 text-zinc-400" />
    ) : (
      <BookOpen className="h-4 w-4 shrink-0 text-zinc-400" />
    );

  const itemLabel = (item: FlatItem): string => {
    if (item.kind === "page") return ro ? item.page.ro : item.page.en;
    if (item.kind === "message")
      return item.hit.recipient + " \u00b7 " + item.hit.status;
    if (item.kind === "contact") {
      const name = [item.hit.first_name, item.hit.last_name]
        .filter(Boolean)
        .join(" ");
      return (name ? name + " \u00b7 " : "") + item.hit.phone_e164;
    }
    if (item.kind === "campaign")
      return item.hit.name + " \u00b7 " + item.hit.status;
    return t(item.guide.titleKey);
  };

  return (
    <div ref={rootRef} className="relative w-full max-w-[520px]">
      <Search
        className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-ink-subtle stroke-[1.75]"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onInputKey}
        placeholder={t("topbar.search_placeholder")}
        aria-label={t("topbar.search_placeholder")}
        className="h-9 w-full rounded-md border border-border bg-surface-subtle/60 pl-10 pr-14 text-caption text-ink placeholder:text-ink-subtle transition-colors focus:border-border-strong focus:bg-surface-card focus:outline-none focus:shadow-focus"
      />
      {isFetching ? (
        <div
          className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-600"
          aria-hidden="true"
        />
      ) : (
        <kbd
          className="pointer-events-none absolute right-2.5 top-1/2 inline-flex h-5 -translate-y-1/2 items-center gap-0.5 rounded border border-border bg-surface-card px-1.5 font-mono text-[10px] font-medium text-ink-muted"
          aria-hidden="true"
        >
          ⌘K
        </kbd>
      )}

      {open && q.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl bg-white/95 shadow-2xl ring-1 ring-zinc-900/[0.08] backdrop-blur-xl">
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {dq.length < 2 ? (
              <p className="px-3 py-5 text-center text-[13px] text-zinc-400">
                {t("searchDialog.hint_min_chars")}
              </p>
            ) : flat.length === 0 && !isFetching ? (
              <p className="px-3 py-5 text-center text-[13px] text-zinc-400">
                {t("searchDialog.empty")}
              </p>
            ) : (
              flat.map((item, i) => {
                const prev = flat[i - 1];
                const showHeader =
                  i === 0 || (prev && prev.kind !== item.kind);
                const key =
                  item.kind === "page"
                    ? "page-" + item.page.path
                    : item.kind === "guide"
                      ? "guide-" + item.guide.href
                      : item.kind + "-" + item.hit.id;
                return (
                  <div key={key}>
                    {showHeader && (
                      <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                        {sectionLabel(item.kind)}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => go(item)}
                      onMouseEnter={() => setIndex(i)}
                      className={
                        "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[14px] transition-colors " +
                        (i === index
                          ? "bg-blue-50 text-blue-900"
                          : "text-zinc-700 hover:bg-zinc-50")
                      }
                    >
                      {itemIcon(item.kind)}
                      <span className="min-w-0 flex-1 truncate">
                        {itemLabel(item)}
                      </span>
                      {i === index && (
                        <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-blue-400" />
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
